<!-- This whole component will be replaced with new image navigation -->
<template>
  <div class="flex-none hidden md:block">
    <!-- Start Bottom Desktop -->
    <div class="w-auto h-auto !bg-opacity-90 !backdrop-blur-lg">
      <div class="relative flex justify-center items-center">
        <!-- Start Bottom Left -->
        <div class="flex items-center gap-2">
          <!-- Reduce max-width by the width of the UI elements bottom right to avoid collision -->

          <UploadButton @click="uploadFile" class="upload-btn sm:h-16 sm:w-16" />

          <div class="overflow-x-scroll overflow-auto" style="max-width: calc(100vw - 350px)">
            <div class="flex gap-2 items-center">
              <div v-for="image in store.images" :key="image.meta.id" @click="store.selectImage(image)">
                <ImageButton
                  v-if="
                    image.previewResult.state === ProcessingState.Error || image.original.state === UploadState.Error
                  "
                  :selected="store.selectedImage === image"
                  class="relative"
                >
                  <div class="bg-typo text-white flex justify-center items-center bg-opacity-80 absolute w-full h-full">
                    <WarningIcon />
                  </div>
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>

                <ImageButton
                  v-else-if="image.previewResult.state === ProcessingState.Processing"
                  :selected="store.selectedImage === image"
                  class="relative"
                >
                  <div class="bg-typo flex justify-center items-center bg-opacity-80 absolute w-full h-full">
                    <LoadingIcon class="text-white animate-spin" />
                  </div>
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>

                <ImageButton v-else :selected="store.selectedImage === image">
                  <HTMLImage
                    :src="previewUrl(image)"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>
              </div>

              <!-- Empty element to have some more space for horizontal scrolling -->
              <div class="block shrink-0 !w-12 !h-12 sm:!w-16 sm:!h-16 pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- End Bottom Left -->
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Image as HTMLImage, ImageButton, WarningIcon, LoadingIcon, UploadButton } from "prism";
import { Image, UploadState } from "@/modules/internal_api/image";
import { ProcessingState } from "@/modules/internal_api/image";
import { rbgRemoveImageV100 } from "kaleido-event-tracker";
import originalPlaceholder from "@assets/images/inline-editor/original_placeholder.jpg";

import Client from "@/modules/internal_api/client";

import { useEditorStore } from "@/stores/editor_store";
const store = useEditorStore();

const requiresDeleteConfirmation = computed(() => {
  const isHdResultPresent = store.selectedImage?.hdResult !== undefined;
  const isHdResultReady = store.selectedImage?.hdResult?.state === ProcessingState.Ready;
  // TODO improvement - filter out error processing states and only check for processing/finished states
  const isMagicBackgroundProcessed = store.selectedImage?.magicBackgroundResults?.size > 0;

  return (isHdResultPresent && !isHdResultReady) || isMagicBackgroundProcessed;
});

const uploadFile = () => {
  window.onUploadFile();
};

const previewUrl = (image: Image) => {
  if (image.meta.preview === originalPlaceholder && image.original.url) return image.original.url;
  return image.meta.preview;
};

const deleteImage = (image: Image, closeDialog?: () => void) => {
  Client.deleteImage(image);
  store.deleteImage(image);
  if (closeDialog) closeDialog();
  window.track("Images", "remove_result", "Remove result");
  rbgRemoveImageV100({ image_id: image.meta.id });
};
</script>

<style scoped lang="scss">
.delete-image-dialog-button {
  background-color: #dd2d00 !important;
  &:hover {
    background-color: #c62801 !important;
  }
}

.delete-image-dialog {
  width: 480px !important;
  max-width: 480px !important;
  max-height: 680px !important;
}
</style>
